import { debounce, isArray, isEmpty, isNull, toNumber } from 'lodash';
import * as React from 'react';
import { useState, useEffect } from 'react';
import Select from 'react-select';
import { isNullOrUndefined } from 'util';

import {
  IControlError,
  IControlSchema,
  IControlSchemaBase,
} from '../../../core/Controls';

import { IDispatcher } from '../../../libs/helpers/Dispatcher';
import { I18NString, tr } from '../../../libs/helpers/Utils';

import ErrorAlert from '../../../libs/controls/components/ErrorAlert';
import Field from '../../../libs/controls/components/Field';
import { UnidataUrlData } from '../../../libs/controls/Grid/interfaces';
import { mapUrlOptions } from '../../utils/mapUrlOptions';
import { useGetOptionsByData } from '../../hooks/useGetOptionsByData';

export interface IDropBoxOptionSchema {
  key: string | number;
  value: I18NString;
}

export interface IDropBoxSchema extends Omit<IControlSchemaBase, 'data'> {
  description: I18NString;
  notification: I18NString;
  options: Array<IDropBoxOptionSchema>;
  value?: any;
  disabled?: boolean;
  multi?: boolean;
  extra?: any;
  widget?: any;
  countries?: Array<any>;
  flows?: Array<any>;
  // TODO: DynamicOptions - Заменить
  dynamicOptions: any;
  data?: UnidataUrlData;
}

export interface IDropBoxProps {
  schema: IDropBoxSchema;
  api: any;
  dispatcher?: IDispatcher;
  error: IControlError | undefined;
  visited: boolean;
  dynamicOptions?: any;
}

type OptionsType = UnidataUrlData['extra'];

const findOptionsById = (arr, id, key) => {
  if (!Array.isArray(arr)) {
    return {};
  }
  return arr?.filter((item) => item?.name === id).find((item) => item?.[key])?.[
    key
  ];
};

/** todo: отупить, вынести конкретную bll в другой слой
 * вывалить всю model-часть можно, например, в entity, относящуюся исключительно к справочникам, через которые dropbox и работает
 */
export const DropBox = (props: IDropBoxProps) => {
  const [openInfo, setOpenInfo] = useState(false);
  const [isOpenNotification, setIsOpenNotification] = useState(true);
  const { getOptions, isLoading, options } = useGetOptionsByData({
    data: props.schema.data,
    api: props.api,
    urlParams: props.schema.urlParams,
  });

  // методы обработки изменения
  const handleChange = (select) => {
    const { dispatcher, schema } = props;

    for (const key in schema.extra.writeValueInId) {
      if (!select?.[key]) {
        continue;
      }
      dispatcher.onAddValueRefresh(
        schema.extra.writeValueInId[key],
        select?.[key]?.toString() || null,
        undefined,
      );
    }
  };

  const handleChangeDebounced = debounce(handleChange, 200);

  useEffect(() => {
    return () => {
      handleChangeDebounced.cancel();
    };
  }, []);

  const onChange = (select) => {
    const { dispatcher, schema } = props;

    // стоит это двинуть в handleDrawDynamicOptions
    if (schema?.extra?.drawDynamicOptions) {
      Object.keys(schema?.extra?.drawDynamicOptions).forEach((key) => {
        dispatcher.setDynamicOption({
          selectType: key,
          options: findOptionsById(
            props.dynamicOptions?.[schema?.widget?.selectType],
            select?.name,
            key,
          ),
        });
      });
    }

    if (isArray(select)) {
      dispatcher.onAddValueRefresh(
        schema.id,
        isEmpty(select) ? null : select,
        undefined,
      );
    } else {
      let value: string;

      switch (true) {
        case isNull(select):
        case toNumber(select.value) === 0:
          value = null;
          break;
        default:
          value = String(select.value);
          break;
      }

      dispatcher.onAddValueRefresh(
        schema.id,
        isNull(value) ? null : value.toString(),
        undefined,
      );

      if (schema?.extra?.writeValueInId) {
        // @ts-ignore
        handleChangeDebounced(select);
      }
    }
  };

  useEffect(() => {
    getOptions();
  }, [props.schema.data]);

  //
  const onClick = () => {
    setOpenInfo(!openInfo);
  };

  const onOpenNotification = () => {
    setIsOpenNotification(!isOpenNotification);
  };

  // render
  const {
    schema: {
      data,
      disabled,
      description,
      notification,
      multi,
      value,
      controlValue,
      options: schemaOptions,
      widget,
      countries,
      flows,
      extra,
    },
    visited,
    error,
    dynamicOptions,
  } = props;

  const errorMessage = visited && error && error.message;

  // TODO: need rebuild and use state options
  let arr = [];

  // TODO: need rebuild and use helper
  if (typeof schemaOptions !== 'undefined') {
    arr = schemaOptions.map((o: IDropBoxOptionSchema) => ({
      value: o.key,
      label: o.value.ru,
    }));
  }

  // УДАЛИТЬ - как перейдут все флоу и кантри
  // upd 2024 - кажется, это всё ещё используется, лучше забросить всё ниже в какой-нибудь хук и забыть про этот кусок, т.к. он вряд ли поменяется
  // TODO: need rebuild and use helper
  // TODO: DRY
  if (
    typeof widget !== 'undefined' &&
    widget.selectType === 'country' &&
    !data?.url
  ) {
    if (typeof countries !== 'undefined')
      arr = countries
        .filter((item) => item.name.length > 0)
        .map((item) => ({
          value: item.asu_id,
          label: item.name,
        }))
        .sort((a, b) => {
          if (a.label < b.label) {
            return -1;
          }
          if (a.label > b.label) {
            return 1;
          }
          return 0;
        });
  }

  // TODO: need rebuild and use helper
  // TODO: need rebuild and use state options
  // TODO: DRY
  if (
    typeof widget !== 'undefined' &&
    widget.selectType === 'flow' &&
    !data?.url
  ) {
    if (typeof flows !== 'undefined')
      arr = flows
        .filter((item) => item.name.length > 0)
        .map((item) => ({
          value: item.id,
          label: item.name,
        }))
        .sort((a, b) => {
          if (a.label < b.label) {
            return -1;
          }
          if (a.label > b.label) {
            return 1;
          }
          return 0;
        });
  }

  // TODO: need rebuild and use state options
  if (
    widget &&
    dynamicOptions &&
    !['flow', 'country'].includes(widget.selectType)
  ) {
    arr = mapUrlOptions(dynamicOptions?.[widget?.selectType]);

    const isExist = arr.some((opt) => opt.value === (controlValue || value));

    if (!isExist && disabled) {
      arr = [
        ...arr,
        { value: controlValue || value, label: controlValue || value },
      ];
    }
  }
  // конец кода, который нужно вынести в отдельный хук и забыть

  return (
    <Field
      {...props.schema}
      status={isNullOrUndefined(controlValue || value) ? 'default' : 'success'}
    >
      {extra && extra.hint && (
        <div
          className="alert alert-info hint"
          dangerouslySetInnerHTML={{ __html: extra.hint }}
        />
      )}
      <div className="input-group">
        {!isLoading ? (
          <Select
            autosize={false}
            multi={multi}
            name="form-field-name"
            className="has-success"
            placeholder="Выберите из списка"
            value={controlValue || value}
            disabled={disabled}
            style={{
              width: '100%',
              borderColor: controlValue || value ? '#4eb43e' : '#ccc',
            }}
            onChange={onChange}
            clearable={false}
            noResultsText="Список пуст"
            options={arr?.length ? arr : options}
          />
        ) : (
          <p> Загрузка...</p>
        )}
        {notification ? (
          <span
            onClick={onOpenNotification}
            className="input-group-addon btn btn-default"
          >
            <span className="glyphicon glyphicon-info-sign" />
          </span>
        ) : null}
        {description ? (
          <span onClick={onClick} className="input-group-addon btn btn-default">
            <span className="glyphicon glyphicon-question-sign" />
          </span>
        ) : null}
      </div>
      {notification && isOpenNotification ? (
        <div
          className="alert alert-warning"
          role="alert"
          dangerouslySetInnerHTML={{
            __html: tr(props.schema.notification),
          }}
        />
      ) : null}
      {description && openInfo && (
        <div
          className="alert alert-warning"
          role="alert"
          dangerouslySetInnerHTML={{
            __html: tr(props.schema.description),
          }}
        />
      )}
      <ErrorAlert errorMessage={errorMessage} />
    </Field>
  );
};

export const createDropBox = (
  schema: IControlSchema,
  api: any,
  dispatcher: IDispatcher = null,
  error: IControlError | undefined,
  visited: boolean,
  dynamicOptions: any,
) => {
  return (
    <DropBox
      schema={schema as IDropBoxSchema}
      api={api}
      dispatcher={dispatcher}
      error={error}
      visited={visited}
      dynamicOptions={dynamicOptions}
    />
  );
};
